import React, { useMemo } from 'react';
import { Typography, Box } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const OperatorEfficiencyChart = ({ paySheetsData, classes }) => {
    // Преобразуем данные для графика
    const chartData = useMemo(() => {
        if (!paySheetsData || paySheetsData.length === 0) return [];
        
        return paySheetsData
            .filter(sheet => sheet.period) // Фильтруем только с периодом
            .map(sheet => {
                // Парсим период "09.2023" -> месяц и год
                const [month, year] = sheet.period.split('.');
                const monthNames = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];
                const monthName = monthNames[parseInt(month) - 1] || month;
                const periodLabel = `${monthName} ${year}`;
                
                // Извлекаем данные из структуры
                // paidTime и talkTime приходят в секундах, конвертируем в часы
                const paidTimeSeconds = parseFloat(sheet.paidTime) || 0;
                const talkTimeSeconds = parseFloat(sheet.talkTime) || 0;
                const occPercent = parseFloat(sheet.occPercent) || 0;
                
                return {
                    period: periodLabel,
                    periodSort: `${year}-${month.padStart(2, '0')}`, // Для сортировки
                    paidTime: Math.round((paidTimeSeconds / 3600) * 10) / 10, // Конвертируем секунды в часы с одним знаком после запятой
                    talkTime: Math.round((talkTimeSeconds / 3600) * 10) / 10, // Конвертируем секунды в часы с одним знаком после запятой
                    occPercent: parseFloat(occPercent.toFixed(2))
                };
            })
            .sort((a, b) => a.periodSort.localeCompare(b.periodSort)); // Сортируем по дате
    }, [paySheetsData]);

    if (!paySheetsData || chartData.length === 0) {
        return (
            <div className={classes.scheduleSection}>
                <Typography className={classes.sectionTitle}>
                    <TrendingUpIcon style={{ fontSize: 18, marginRight: 8, verticalAlign: 'middle' }} />
                    График эффективности
                </Typography>
                <Typography style={{ color: '#666', fontSize: '14px', marginTop: 8 }}>
                    Нет данных для отображения
                </Typography>
            </div>
        );
    }

    return (
        <div className={classes.scheduleSection}>
            <Typography className={classes.sectionTitle}>
                <TrendingUpIcon style={{ fontSize: 18, marginRight: 8, verticalAlign: 'middle' }} />
                График эффективности
            </Typography>
            <Box style={{ marginTop: 16, width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        style={{ width: '100%', maxWidth: '700px', maxHeight: '70vh', aspectRatio: 1.618 }}
                        margin={{top: 0, right: 0, left: 0, bottom: 0}}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                            dataKey="period" 
                            stroke="#666"
                            style={{ fontSize: '0.75rem', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
                        />
                        <YAxis 
                            yAxisId="left"
                            stroke="#1976d2"
                            style={{ fontSize: '12px' }}
                        />
                        <YAxis 
                            yAxisId="right"
                            orientation="right"
                            stroke="#4caf50"
                            style={{ fontSize: '12px' }}
                        />
                        <Tooltip 
                            formatter={(value, name) => {
                                if (name === 'occPercent') {
                                    return [
                                        <span style={{ fontWeight: 500, fontSize: '0.75rem', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>{`${value}%`}</span>, 
                                        'OCC %'
                                    ];
                                }
                                return [
                                    <span style={{ fontWeight: 500, fontSize: '0.75rem', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>{`${value} ч`}</span>, 
                                    name === 'paidTime' ? `В линии` : `В разговоре`];
                            }}
                            labelStyle={{ fontWeight: 500, fontSize: '0.75rem', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}
                        />
                        <Legend 
                            formatter={(value) => {
                                const labels = {
                                    'paidTime': 'В линии (ч)',
                                    'talkTime': 'В разговоре (ч)',
                                    'occPercent': 'OCC (%)'
                                };
                                return <span style={{ fontWeight: 500, fontSize: '0.75rem', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>{labels[value] || value}</span>;
                            }}
                        />
                        <Line 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="paidTime" 
                            stroke="#1976d2" 
                            strokeWidth={2}
                            dot={{ r: 1 }}
                            activeDot={{ r: 3 }}
                            name="paidTime"
                        />
                        <Line 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="talkTime" 
                            stroke="#ff9800" 
                            strokeWidth={2}
                            dot={{ r: 1 }}
                            activeDot={{ r: 3 }}
                            name="talkTime"
                        />
                        <Line 
                            yAxisId="right"
                            type="monotone" 
                            dataKey="occPercent" 
                            stroke="#4caf50" 
                            strokeWidth={2}
                            dot={{ r: 1 }}
                            activeDot={{ r: 3 }}
                            name="occPercent"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Box>
        </div>
    );
};

export default OperatorEfficiencyChart;

